import * as React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "lib/utils";
import { EmptyState } from "components/ui/empty-state";

export interface ErrorStateProps {
    title?: React.ReactNode;
    description?: React.ReactNode;
    error?: unknown;
    onRetry?: () => void;
    retryLabel?: string;
    /** Removes the card chrome */
    bare?: boolean;
    className?: string;
}

function messageOf(error: unknown): string | undefined {
    if (!error) return undefined;
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;
    const anyErr = error as any;
    return anyErr?.response?.data?.message ?? anyErr?.message ?? undefined;
}

/**
 * Canonical failure state. Shows a human sentence first; the raw error text is
 * demoted to a details disclosure so it never becomes the headline.
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
    title = "Something went wrong",
    description = "We couldn't load this content. Check your connection and try again.",
    error,
    onRetry,
    retryLabel = "Try again",
    bare,
    className,
}) => {
    const detail = messageOf(error);
    return (
        <EmptyState
            variant="error"
            icon={AlertTriangle}
            title={title}
            description={description}
            bare={bare}
            className={className}
            primaryAction={
                onRetry ? { label: retryLabel, onClick: onRetry, icon: <RefreshCw className="w-4 h-4" /> } : null
            }
        >
            {detail && (
                <details className="mt-5 max-w-lg w-full text-left">
                    <summary className="cursor-pointer text-meta font-medium text-content-muted hover:text-content-secondary focus-ring rounded">
                        Technical details
                    </summary>
                    <pre className="mt-2 p-3 rounded-lg bg-surface-sunken border border-line text-2xs text-content-secondary whitespace-pre-wrap break-words overflow-auto max-h-40">
                        {detail}
                    </pre>
                </details>
            )}
        </EmptyState>
    );
};

interface BoundaryProps {
    children: React.ReactNode;
    /** Rendered instead of the default panel; receives the error and a reset fn */
    fallback?: (error: Error, reset: () => void) => React.ReactNode;
    /** Shown as the panel title when using the default fallback */
    title?: string;
    /** Reported to your monitoring layer */
    onError?: (error: Error, info: React.ErrorInfo) => void;
    /** Remount the subtree when any of these change */
    resetKeys?: unknown[];
    className?: string;
}

interface BoundaryState {
    error: Error | null;
}

/**
 * Single error boundary for the app. `support/ErrorBoundary` and
 * `templates/ErrorBoundary` now delegate here, so a crash renders the same
 * recoverable panel wherever it happens instead of two different screens.
 */
export class AppErrorBoundary extends React.Component<BoundaryProps, BoundaryState> {
    state: BoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): BoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        this.props.onError?.(error, info);
        if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.error("[AppErrorBoundary]", error, info);
        }
    }

    componentDidUpdate(prev: BoundaryProps) {
        const { resetKeys } = this.props;
        if (!this.state.error || !resetKeys) return;
        const prevKeys = prev.resetKeys ?? [];
        const changed =
            prevKeys.length !== resetKeys.length ||
            resetKeys.some((key, i) => !Object.is(key, prevKeys[i]));
        if (changed) this.reset();
    }

    reset = () => this.setState({ error: null });

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;
        if (this.props.fallback) return this.props.fallback(error, this.reset);

        return (
            <div className={cn("p-4 sm:p-6", this.props.className)}>
                <ErrorState
                    title={this.props.title ?? "This section failed to load"}
                    description="The rest of the app is still working. You can retry, or reload the page if the problem persists."
                    error={error}
                    onRetry={this.reset}
                />
            </div>
        );
    }
}

export default ErrorState;
